'use client';

import {createContext, useContext, useMemo, type ReactNode} from 'react';
import type {RichTextAssetFragment, InformationLinkButtonFieldsFragment, FaqSectionFieldsFragment} from '@generated/graphql';

export type TopicCtaAreaRefEntry = {
  __typename: 'TopicCtaArea';
  sys: {id: string};
  heading?: string | null;
  breadText?: string | null;
  linkTo?: InformationLinkButtonFieldsFragment | null;
};

export type ActiveLinkRefEntry = {
  __typename: 'ActiveLink';
  sys: {id: string};
  label?: string | null;
  activeLinkUrl?: string | null;
  matchingUrlPath?: string | null;
  title?: string | null;
};

export type MediaUploadRefEntry = {
  __typename: 'MediaUpload';
  sys: {id: string};
  heading?: string | null;
  title?: string | null;
  mediaFiles?: {
    url?: string | null;
    contentType?: string | null;
    title?: string | null;
  }[];
};

export type FaqSectionRefEntry = FaqSectionFieldsFragment & {
  __typename: 'FaqSection';
  sys: {id: string};
};

export type RichTextRefEntry =
  | TopicCtaAreaRefEntry
  | ActiveLinkRefEntry
  | MediaUploadRefEntry
  | FaqSectionRefEntry;

type RichTextRefsContextValue = {
  assetsById: Map<string, RichTextAssetFragment>;
  entriesById: Map<string, RichTextRefEntry>;
};

const EMPTY: RichTextRefsContextValue = {
  assetsById: new Map(),
  entriesById: new Map(),
};

const RichTextRefsContext = createContext<RichTextRefsContextValue>(EMPTY);

export const useRichTextRefs = () => useContext(RichTextRefsContext);

type ProviderProps = {
  assets: RichTextAssetFragment[];
  entries: RichTextRefEntry[];
  children: ReactNode;
};

// IDs of entries currently being rendered above this point. Entries like FAQs
// contain rich text that can embed entries again, so an entry that embeds
// itself, directly or via another entry, would otherwise recurse forever.
const EmbeddedEntryPathContext = createContext<ReadonlySet<string>>(new Set());

export const useEmbeddedEntryPath = () => useContext(EmbeddedEntryPathContext);

export function EmbeddedEntryPathProvider({entryId, children}: {entryId: string; children: ReactNode}) {
  const parentPath = useEmbeddedEntryPath();
  const value = useMemo(() => new Set([...parentPath, entryId]), [parentPath, entryId]);
  return (
    <EmbeddedEntryPathContext.Provider value={value}>
      {children}
    </EmbeddedEntryPathContext.Provider>
  );
}

export function RichTextRefsProvider({assets, entries, children}: ProviderProps) {
  const value = useMemo<RichTextRefsContextValue>(
    () => ({
      assetsById: new Map(assets.map((a) => [a.sys.id, a])),
      entriesById: new Map(entries.map((e) => [e.sys.id, e])),
    }),
    [assets, entries]
  );
  return (
    <RichTextRefsContext.Provider value={value}>
      {children}
    </RichTextRefsContext.Provider>
  );
}
